import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { catalogApi } from '../../services/api/catalog.api';
import { CatalogProgram, CatalogCollege, CatalogExam } from '../../types/catalog.response';
import AccountSettings from "./AccountSettings";
import ProgramSettings from "./ProgramSettings";
import ExamSettings from "./ExamSettings";

type Tab = 'account' | 'program' | 'exam';

const tabs: { id: Tab; label: string }[] = [
    { id: 'account', label: 'Account' },
    { id: 'program', label: 'Program' },
    { id: 'exam', label: 'Exams' },
];

export default function Settings() {
    const { user, refreshAuth } = useAuth();
    const [activeTab, setActiveTab] = useState<Tab>('account');
    const [programs, setPrograms] = useState<CatalogProgram[]>([]);
    const [colleges, setColleges] = useState<CatalogCollege[]>([]);
    const [catalogExams, setCatalogExams] = useState<CatalogExam[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                setCatalogLoading(true);
                setError(null);
                const [progs, cols, exams] = await Promise.all([
                    catalogApi.getPrograms(),
                    catalogApi.getColleges(),
                    catalogApi.getExams(),
                ]);
                setPrograms(progs);
                setColleges(cols);
                setCatalogExams(exams);
            } catch (err) {
                setError('Failed to load settings data. Please refresh the page.');
            } finally {
                setCatalogLoading(false);
            }
        };
        fetchCatalog();
    }, []);

    if (!user) return null;

    return (
        <div className="max-w-2xl mx-auto px-6 py-10">
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">Settings</h1>

            {/* Tab bar */}
            <div className="flex border-b border-gray-200 mb-8">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-5 py-3 text-sm font-medium transition-colors relative ${
                            activeTab === tab.id
                                ? 'text-gray-900'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                        {activeTab === tab.id && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t" />
                        )}
                    </button>
                ))}
            </div>

            {error && (
                <div className="bg-red-50 p-4 rounded mb-6 border border-red-100">
                    <p className="text-red-700 font-medium">{error}</p>
                </div>
            )}

            {/* Tab content */}
            {activeTab === 'account' && (
                <AccountSettings user={user} refreshAuth={refreshAuth} />
            )}
            {activeTab === 'program' && (
                <ProgramSettings
                    user={user}
                    programs={programs}
                    colleges={colleges}
                    catalogLoading={catalogLoading}
                    refreshAuth={refreshAuth}
                />
            )}
            {activeTab === 'exam' && (
                <ExamSettings
                    catalogExams={catalogExams}
                    catalogLoading={catalogLoading}
                />
            )}
        </div>
    );
}
